import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ai, conflicts } from '@lawbee/domain';
import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogContent,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingState,
  PageHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  cn,
  formatDateTime,
  formatRelative,
  segmentedItemClass,
  segmentedListClass,
  useToast,
} from '@lawbee/ui';
import { ArrowRight, Download, FileText, Inbox, Mail, Phone, Search, Send, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { api, apiUrl, errorMessage, isApiError } from '../../../lib/api';
import { formatBytes, useApiMutation, useParam, useSearchParam } from '../../../lib/hooks';
import { can, useMe } from '../../../lib/me';
import type { ConflictHit, IntakeDetail, IntakeStatus, IntakeSummary } from '../../../lib/types';
import { ConflictDecisionStatus, DefinitionList, IntakeStatusBadge, Panel, formatAnswer, fullName, roleLabel } from './shared';

// ---------------------------------------------------------------------------
// Review queue
// ---------------------------------------------------------------------------

const FILTERS: { value: '' | IntakeStatus; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'new', label: 'New' },
  { value: 'needs_info', label: 'Needs info' },
  { value: 'in_review', label: 'In review' },
  { value: 'converted', label: 'Converted' },
  { value: 'declined', label: 'Declined' },
];

export function IntakesPage() {
  const navigate = useNavigate();
  const initialStatus = useSearchParam('status');
  const [status, setStatus] = useState<'' | IntakeStatus>(() => (FILTERS.find((f) => f.value === initialStatus)?.value ?? ''));
  const q = useQuery({
    queryKey: ['intakes', status],
    queryFn: () => api.get<{ intakes: IntakeSummary[] }>(`/api/intake/intakes${status ? `?status=${status}` : ''}`),
  });
  const intakes = q.data?.intakes ?? [];
  const open = (id: string) => void navigate({ to: '/intakes/$id', params: { id } });

  return (
    <div>
      <PageHeader
        title="Intakes"
        description="New enquiries from your intake forms. Review, run the conflict check, then convert to a matter."
        actions={
          <Button asChild variant="outline">
            <Link to="/intake-forms">
              <FileText aria-hidden /> Intake forms
            </Link>
          </Button>
        }
      />
      <div className="mb-5">
        <div role="tablist" aria-label="Filter by status" className={segmentedListClass}>
          {FILTERS.map((f) => (
            <button key={f.value || 'all'} type="button" role="tab" aria-selected={status === f.value} className={segmentedItemClass(status === f.value)} onClick={() => setStatus(f.value)}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {q.isLoading ? (
        <LoadingState label="Loading intakes" />
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : intakes.length === 0 ? (
        <EmptyState
          icon={<Inbox />}
          title={status ? `No ${FILTERS.find((f) => f.value === status)?.label.toLowerCase()} intakes` : 'No intakes yet'}
          description="Share an intake form link on your website. Every submission lands here for review."
          action={
            <Button asChild variant="outline">
              <Link to="/intake-forms">Manage intake forms</Link>
            </Button>
          }
        />
      ) : (
        <>
          {/* Cards on phones */}
          <ul className="space-y-2 md:hidden">
            {intakes.map((i) => (
              <li key={i.id}>
                <Link to="/intakes/$id" params={{ id: i.id }} className="block rounded-xl bg-card p-4 shadow-card transition-colors hover:bg-muted/40">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-medium">{fullName(i.firstName, i.lastName)}</p>
                      <p className="truncate text-[13px] text-muted-foreground">{i.matterTypeName ?? i.formName ?? 'General enquiry'}</p>
                    </div>
                    <IntakeStatusBadge status={i.status} />
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[13px]">
                    <ConflictDecisionStatus decision={i.conflictDecision} />
                    {i.missingCount > 0 ? (
                      <Badge variant="warning">
                        <span className="figures">{i.missingCount}</span> missing
                      </Badge>
                    ) : null}
                    <span className="figures ml-auto text-muted-foreground">{formatRelative(i.createdAt)}</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
          {/* Table on wider screens */}
          <div className="hidden overflow-hidden rounded-xl bg-card shadow-card md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Form / matter type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Missing</TableHead>
                  <TableHead>Conflict check</TableHead>
                  <TableHead className="text-right">Received</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {intakes.map((i) => (
                  <TableRow key={i.id} className="cursor-pointer" onClick={() => open(i.id)}>
                    <TableCell>
                      <Link to="/intakes/$id" params={{ id: i.id }} className="font-medium hover:underline" onClick={(e) => e.stopPropagation()}>
                        {fullName(i.firstName, i.lastName)}
                      </Link>
                      {i.email ? <div className="text-[12px] text-muted-foreground">{i.email}</div> : null}
                    </TableCell>
                    <TableCell>
                      <div>{i.formName ?? '—'}</div>
                      {i.matterTypeName ? <div className="text-[12px] text-muted-foreground">{i.matterTypeName}</div> : null}
                    </TableCell>
                    <TableCell>
                      <IntakeStatusBadge status={i.status} />
                    </TableCell>
                    <TableCell>
                      {i.missingCount > 0 ? (
                        <Badge variant="warning">
                          <span className="figures">{i.missingCount}</span> missing
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <ConflictDecisionStatus decision={i.conflictDecision} />
                    </TableCell>
                    <TableCell className="figures whitespace-nowrap text-right text-muted-foreground" title={formatDateTime(i.createdAt)}>
                      {formatRelative(i.createdAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Intake detail
// ---------------------------------------------------------------------------

const SOURCE_LABELS: Record<ConflictHit['source'], string> = {
  contact: 'Client/contact',
  matter_party: 'Matter party',
  intake: 'Prospective client (intake)',
  intake_party: 'Party in another intake',
};

export function IntakeDetailPage() {
  const id = useParam('id');
  const q = useQuery({ queryKey: ['intake', id], queryFn: () => api.get<IntakeDetail>(`/api/intake/intakes/${id}`), enabled: !!id });

  if (q.isLoading) return <LoadingState label="Loading intake" />;
  if (q.error || !q.data) {
    return (
      <div>
        <PageHeader title="Intake" breadcrumbs={<Link to="/intakes">Intakes</Link>} />
        <ErrorState title={isApiError(q.error, 'not_found') ? 'Intake not found' : 'Could not load this intake'} error={q.error} onRetry={() => void q.refetch()} />
      </div>
    );
  }
  return <IntakeDetailView data={q.data} />;
}

function IntakeDetailView({ data }: { data: IntakeDetail }) {
  const { intake, files } = data;
  const name = fullName(intake.firstName, intake.lastName);
  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Link to="/intakes" className="hover:text-foreground">
            Intakes
          </Link>
        }
        title={name}
        description={
          <span className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
            <IntakeStatusBadge status={intake.status} />
            {intake.email ? (
              <a href={`mailto:${intake.email}`} className="inline-flex items-center gap-1.5 hover:text-foreground">
                <Mail className="size-3.5" aria-hidden /> {intake.email}
              </a>
            ) : null}
            {intake.phone ? (
              <a href={`tel:${intake.phone}`} className="figures inline-flex items-center gap-1.5 hover:text-foreground">
                <Phone className="size-3.5" aria-hidden /> {intake.phone}
              </a>
            ) : null}
            <span>{intake.formName ?? 'No form'}</span>
            <span>
              Received <span className="figures">{formatDateTime(intake.createdAt)}</span>
            </span>
          </span>
        }
        actions={<IntakeActions data={data} />}
      />

      {intake.matterId ? (
        <Alert variant="success" title="Converted to a matter" className="mb-6">
          <Link to="/matters/$id" params={{ id: intake.matterId }} className="inline-flex items-center gap-1 font-medium text-foreground underline-offset-4 hover:underline">
            Open the matter <ArrowRight className="size-3.5" aria-hidden />
          </Link>
        </Alert>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="min-w-0 space-y-6">
          <AnswersPanel data={data} />
          <MissingPanel data={data} />
          {files.length > 0 ? <FilesPanel data={data} /> : null}
        </div>
        <div className="min-w-0 space-y-6">
          <ConflictPanel data={data} />
          <SummaryPanel data={data} />
        </div>
      </div>
    </div>
  );
}

function useIntakeInvalidate(id: string) {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['intake', id] }),
      qc.invalidateQueries({ queryKey: ['intakes'] }),
      qc.invalidateQueries({ queryKey: ['dashboard'] }),
    ]);
}

function IntakeActions({ data }: { data: IntakeDetail }) {
  const { intake } = data;
  const id = intake.id;
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const invalidate = useIntakeInvalidate(id);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [convertOpen, setConvertOpen] = useState(false);
  const [title, setTitle] = useState('');

  const status = useApiMutation((body: { status: 'in_review' | 'declined'; reason?: string }) => api.post(`/api/intake/intakes/${id}/status`, body), {
    success: (_d) => 'Intake updated',
    onSuccess: () => {
      void invalidate();
      setDeclineOpen(false);
    },
  });

  const convert = useMutation({
    mutationFn: () => api.post<{ matterId: string }>(`/api/intake/intakes/${id}/convert`, title.trim() ? { title: title.trim() } : {}),
    onSuccess: async (res) => {
      await Promise.all([invalidate(), qc.invalidateQueries({ queryKey: ['matters'] })]);
      toast.success('Matter opened');
      setConvertOpen(false);
      void navigate({ to: '/matters/$id', params: { id: res.matterId } });
    },
    onError: (err) => {
      if (isApiError(err, 'limit_reached')) toast.error('Open matter limit reached', errorMessage(err));
      else toast.error('Could not convert', errorMessage(err));
    },
  });

  const closed = intake.status === 'converted' || intake.status === 'declined';
  const cleared = intake.conflictDecision === 'cleared';

  if (intake.status === 'converted' && intake.matterId) {
    return (
      <Button asChild>
        <Link to="/matters/$id" params={{ id: intake.matterId }}>
          Open matter <ArrowRight aria-hidden />
        </Link>
      </Button>
    );
  }

  return (
    <>
      {!closed ? (
        <Button variant="outline" onClick={() => setDeclineOpen(true)}>
          Decline
        </Button>
      ) : null}
      {intake.status !== 'in_review' && !closed ? (
        <Button variant="outline" loading={status.isPending && status.variables?.status === 'in_review'} onClick={() => status.mutate({ status: 'in_review' })}>
          Mark in review
        </Button>
      ) : null}
      {intake.status === 'declined' ? (
        <Button variant="outline" loading={status.isPending} onClick={() => status.mutate({ status: 'in_review' })}>
          Reopen for review
        </Button>
      ) : null}
      {intake.status !== 'declined' ? (
        <Button disabled={!cleared} title={cleared ? undefined : 'Record a "cleared" conflict decision first'} onClick={() => setConvertOpen(true)}>
          Convert to matter
        </Button>
      ) : null}

      <Dialog open={declineOpen} onOpenChange={setDeclineOpen}>
        <DialogContent
          title="Decline this intake"
          description="The intake stays on file for your conflict records. The prospect is not emailed."
          footer={
            <>
              <Button variant="outline" onClick={() => setDeclineOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" loading={status.isPending} onClick={() => status.mutate({ status: 'declined', reason: reason.trim() || undefined })}>
                Decline intake
              </Button>
            </>
          }
        >
          <Field label="Reason (internal)" htmlFor="decline-reason">
            <Textarea id="decline-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Outside our practice areas, conflict, not a fit…" />
          </Field>
        </DialogContent>
      </Dialog>

      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent
          title="Convert to matter"
          description="Opens a matter with this person as the client. Parties and uploaded files are copied over."
          footer={
            <>
              <Button variant="outline" onClick={() => setConvertOpen(false)}>
                Cancel
              </Button>
              <Button loading={convert.isPending} onClick={() => convert.mutate()}>
                Open matter
              </Button>
            </>
          }
        >
          <Field label="Matter title" htmlFor="convert-title" description="Leave blank to use the client name and the type of help requested.">
            <Input id="convert-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={`${fullName(intake.firstName, intake.lastName)} - ${intake.matterTypeName ?? 'Matter'}`} />
          </Field>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AnswersPanel({ data }: { data: IntakeDetail }) {
  const { intake, form, labels } = data;
  const fieldLabel = (key: string) => labels[key] ?? form.fields.find((f) => f.key === key)?.label ?? key.replace(/_/g, ' ');
  const order = form.fields.map((f) => f.key);
  const keys = Object.keys(intake.answers)
    .filter((k) => !['first_name', 'last_name', 'email', 'phone'].includes(k))
    .sort((a, b) => {
      const ia = order.indexOf(a);
      const ib = order.indexOf(b);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });
  return (
    <Panel title="Answers" description={intake.matterTypeName ?? undefined}>
      {keys.length === 0 ? (
        <p className="text-sm text-muted-foreground">No answers beyond contact details.</p>
      ) : (
        <DefinitionList items={keys.map((k) => ({ label: fieldLabel(k), value: <span className="whitespace-pre-line">{formatAnswer(intake.answers[k])}</span> }))} />
      )}
    </Panel>
  );
}

function MissingPanel({ data }: { data: IntakeDetail }) {
  const { intake } = data;
  const invalidate = useIntakeInvalidate(intake.id);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const send = useApiMutation(() => api.post(`/api/intake/intakes/${intake.id}/request-info`, message.trim() ? { message: message.trim() } : {}), {
    success: 'Follow-up request sent',
    errorTitle: 'Could not send the request',
    onSuccess: () => {
      void invalidate();
      setOpen(false);
      setMessage('');
    },
  });
  if (intake.missing.length === 0 && !intake.followupSentAt) return null;
  const closed = intake.status === 'converted' || intake.status === 'declined';
  return (
    <Panel
      title="Missing information"
      description={intake.missing.length > 0 ? 'Required answers the prospect left blank.' : 'Everything requested has been provided.'}
      actions={
        intake.missing.length > 0 && !closed && intake.email ? (
          <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
            <Send aria-hidden /> {intake.followupSentAt ? 'Send again' : 'Send follow-up request'}
          </Button>
        ) : null
      }
    >
      {intake.missing.length > 0 ? (
        <ul className="space-y-1.5 text-sm">
          {intake.missing.map((m) => (
            <li key={m.field} className="flex items-center gap-2">
              <span className="size-1.5 shrink-0 rounded-full bg-warning" aria-hidden />
              {m.label}
            </li>
          ))}
        </ul>
      ) : null}
      {intake.followupSentAt ? (
        <p className="mt-3 text-[13px] text-muted-foreground">
          Follow-up sent <span className="figures">{formatDateTime(intake.followupSentAt)}</span>
        </p>
      ) : null}
      {!intake.email && intake.missing.length > 0 ? <p className="mt-3 text-[13px] text-muted-foreground">No email address on file, so a follow-up link cannot be sent. Call the prospect instead.</p> : null}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          title="Ask for the missing details"
          description={`${intake.email ?? 'The prospect'} receives a secure link to a short form with only the missing questions.`}
          footer={
            <>
              <Button variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button loading={send.isPending} onClick={() => send.mutate(undefined)}>
                Send request
              </Button>
            </>
          }
        >
          <Field label="Personal message (optional)" htmlFor="followup-message">
            <Textarea id="followup-message" rows={4} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Thanks for reaching out. Before your consultation we need a few more details." />
          </Field>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}

function FilesPanel({ data }: { data: IntakeDetail }) {
  const { intake, files, labels } = data;
  return (
    <Panel title="Files">
      <ul className="divide-y divide-border/70">
        {files.map((f) => (
          <li key={f.id} className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{f.filename}</p>
              <p className="text-[12px] text-muted-foreground">
                {labels[f.fieldKey] ?? f.fieldKey} · <span className="figures">{formatBytes(f.size)}</span>
              </p>
            </div>
            <Button asChild size="sm" variant="outline">
              <a href={apiUrl(`/api/intake/intakes/${intake.id}/files/${f.id}`)} download aria-label={`Download ${f.filename}`}>
                <Download aria-hidden /> Download
              </a>
            </Button>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function SummaryPanel({ data }: { data: IntakeDetail }) {
  const { intake } = data;
  const invalidate = useIntakeInvalidate(intake.id);
  const generate = useApiMutation(() => api.post<{ summary: string; model: string; label: string }>(`/api/intake/intakes/${intake.id}/summary`), {
    success: 'Summary ready',
    errorTitle: 'Could not summarize',
    onSuccess: () => void invalidate(),
  });
  return (
    <Panel
      title={
        <span className="inline-flex items-center gap-2">
          <Sparkles className="size-4 text-muted-foreground" aria-hidden /> Intake summary
        </span>
      }
      actions={
        <Button size="sm" variant="outline" loading={generate.isPending} onClick={() => generate.mutate(undefined)}>
          {intake.aiSummary ? 'Regenerate' : 'Generate'}
        </Button>
      }
    >
      <Alert variant="warning" className="mb-4">
        {ai.NOT_LEGAL_ADVICE_LABEL}
      </Alert>
      {intake.aiSummary ? (
        <>
          <p className="whitespace-pre-line text-sm leading-relaxed">{intake.aiSummary}</p>
          <p className="mt-3 text-[12px] text-muted-foreground">
            {intake.aiSummaryModel === 'stub' ? 'Generated without AI (AI is off for this firm)' : intake.aiSummaryModel ? `Model: ${intake.aiSummaryModel}` : null}
          </p>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">No summary yet. Generate one to get a quick read of the answers before you open the details.</p>
      )}
    </Panel>
  );
}

function ConflictPanel({ data }: { data: IntakeDetail }) {
  const me = useMe();
  const { intake } = data;
  const invalidate = useIntakeInvalidate(intake.id);
  const canDecide = can(me, 'conflict_decision');
  const [decision, setDecision] = useState<'cleared' | 'potential_conflict' | null>(intake.conflictDecision);
  const [note, setNote] = useState(intake.conflictNote ?? '');

  const search = useApiMutation(() => api.post<{ hits: ConflictHit[]; searchedAt: string; disclaimer: string }>(`/api/intake/intakes/${intake.id}/conflict-search`), {
    success: (res) => (res.hits.length === 0 ? 'No possible matches found' : `${res.hits.length} possible ${res.hits.length === 1 ? 'match' : 'matches'} to review`),
    errorTitle: 'Conflict search failed',
    onSuccess: () => void invalidate(),
  });
  const decide = useApiMutation((body: { decision: 'cleared' | 'potential_conflict'; note: string }) => api.post(`/api/intake/intakes/${intake.id}/conflict-decision`, body), {
    success: 'Conflict decision recorded',
    errorTitle: 'Could not record the decision',
    onSuccess: () => void invalidate(),
  });

  const searched: { name: string; role: string }[] = [{ name: fullName(intake.firstName, intake.lastName), role: 'prospective_client' }, ...intake.parties];
  const results = intake.conflictResults;
  const hits = results?.hits ?? [];

  return (
    <Panel title="Ready for your conflict check" description="Search every name in this intake against your clients, matter parties and past intakes.">
      <div className="space-y-5">
        <div>
          <h4 className="mb-2 text-[13px] font-medium text-muted-foreground">Names to check</h4>
          <ul className="flex flex-wrap gap-2">
            {searched.map((p, i) => (
              <li key={`${p.name}-${i}`} className="rounded-lg bg-muted px-2.5 py-1 text-[13px]">
                <span className="font-medium">{p.name}</span> <span className="text-muted-foreground">({roleLabel(p.role)})</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button loading={search.isPending} onClick={() => search.mutate(undefined)} variant={results ? 'outline' : 'primary'}>
            <Search aria-hidden /> {results ? 'Run search again' : 'Run search'}
          </Button>
          {results?.searchedAt ? (
            <span className="text-[13px] text-muted-foreground">
              Last searched <span className="figures">{formatDateTime(results.searchedAt)}</span>
            </span>
          ) : null}
        </div>

        {results ? (
          hits.length === 0 ? (
            <EmptyState className="bg-muted/40 py-8" title="No possible matches found" description="None of the names above matched your records." />
          ) : (
            <div className="-mx-6 sm:mx-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Matter</TableHead>
                    <TableHead>Searched name</TableHead>
                    <TableHead className="text-right">Score</TableHead>
                    <TableHead>Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {hits.map((h, i) => (
                    <TableRow key={`${h.source}-${h.id}-${h.searched}-${i}`}>
                      <TableCell className="font-medium">
                        {h.name}
                        {h.role ? <div className="text-[12px] font-normal text-muted-foreground">{roleLabel(h.role)}</div> : null}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{SOURCE_LABELS[h.source] ?? h.source}</TableCell>
                      <TableCell>
                        {h.matterId ? (
                          <Link to="/matters/$id" params={{ id: h.matterId }} className="figures text-accent-foreground underline-offset-4 hover:underline">
                            {h.matterLabel ?? 'Open matter'}
                          </Link>
                        ) : h.intakeId ? (
                          <Link to="/intakes/$id" params={{ id: h.intakeId }} className="text-accent-foreground underline-offset-4 hover:underline">
                            View intake
                          </Link>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>{h.searched}</TableCell>
                      <TableCell className="figures text-right">{h.score}</TableCell>
                      <TableCell className="min-w-44 text-[13px]">{conflicts.REASON_LABELS[h.reason] ?? h.reason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )
        ) : null}

        <p className="text-[12px] leading-relaxed text-muted-foreground">{conflicts.CONFLICT_DISCLAIMER}</p>

        <div className="space-y-3 border-t pt-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h4 className="text-sm font-semibold">Decision</h4>
            <ConflictDecisionStatus decision={intake.conflictDecision} />
          </div>
          {intake.conflictDecision ? (
            <p className="text-[13px] text-muted-foreground">
              Recorded{intake.conflictReviewedBy ? ` by ${intake.conflictReviewedBy}` : ''}
              {intake.conflictReviewedAt ? (
                <>
                  {' '}
                  on <span className="figures">{formatDateTime(intake.conflictReviewedAt)}</span>
                </>
              ) : null}
              {intake.conflictNote ? <span className="mt-1 block whitespace-pre-line text-foreground">{intake.conflictNote}</span> : null}
            </p>
          ) : null}

          {canDecide ? (
            results ? (
              <form
                className="space-y-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (decision) decide.mutate({ decision, note: note.trim() });
                }}
              >
                <fieldset>
                  <legend className="sr-only">Conflict decision</legend>
                  <div role="radiogroup" className={segmentedListClass}>
                    {(
                      [
                        { value: 'cleared', label: 'Cleared' },
                        { value: 'potential_conflict', label: 'Potential conflict' },
                      ] as const
                    ).map((o) => (
                      <button key={o.value} type="button" role="radio" aria-checked={decision === o.value} className={segmentedItemClass(decision === o.value)} onClick={() => setDecision(o.value)}>
                        {o.label}
                      </button>
                    ))}
                  </div>
                </fieldset>
                <Field label="Note" htmlFor="conflict-note" description="Why you cleared it, or what needs attention. Kept with the intake.">
                  <Textarea id="conflict-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
                </Field>
                <div className="flex justify-end">
                  <Button type="submit" disabled={!decision} loading={decide.isPending}>
                    Record decision
                  </Button>
                </div>
              </form>
            ) : (
              <p className="text-[13px] text-muted-foreground">Run the search before recording a decision.</p>
            )
          ) : (
            <p className={cn('text-[13px] text-muted-foreground')}>A lawyer or owner records the decision.</p>
          )}
        </div>
      </div>
    </Panel>
  );
}
